import type { ErrorInfo, ReactNode } from "react";
import { ErrorBoundary as ReactErrorBoundary, type ErrorBoundaryPropsWithComponent } from "react-error-boundary";

import { useFormatMessage } from "@skilitics/localization";

import { ErrorDialog } from "./ErrorDialog";

export class RenderError extends Error {
  name = "RenderError";
  info: ErrorInfo;

  constructor(error: unknown, info: ErrorInfo) {
    super("Error during render", { cause: error });
    this.info = info;
  }
}

const handleError = (error: unknown, info: ErrorInfo) => {
  reportError(new RenderError(error, info));
};

export interface ErrorBoundaryProps extends Partial<
  Pick<ErrorBoundaryPropsWithComponent, "onError" | "FallbackComponent">
> {
  resetKeys?: unknown[];
  children?: ReactNode;
}

export const ErrorBoundary = ({
  resetKeys,
  children,
  FallbackComponent = ErrorView,
  onError = handleError,
}: ErrorBoundaryProps) => {
  return (
    <ReactErrorBoundary resetKeys={resetKeys} FallbackComponent={FallbackComponent} onError={onError}>
      {children}
    </ReactErrorBoundary>
  );
};

interface RootErrorViewProps {
  error: unknown;
}

const ErrorView = ({ error }: RootErrorViewProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  return <ErrorDialog text={`${error}`} title={formatMessage("title")} />;
};

const formatStrings = {
  title: { en: "An unexpected error has occurred. Please try refreshing the page." },
};
